import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { localize } from "@/lib/localizedFields";
import type { Database } from "@/integrations/supabase/types";

export type IntegrationPage = Database["public"]["Tables"]["integration_pages"]["Row"];

async function withTranslations(rows: IntegrationPage[], locale: string): Promise<(IntegrationPage & { translated: boolean })[]> {
  if (locale === "en" || !rows.length) return rows.map((r) => ({ ...r, translated: false }));
  const { data } = await supabase.from("content_translations").select("record_id,fields").eq("table_name", "integration_pages").eq("locale", locale).in("record_id", rows.map((r) => r.id));
  const m = new Map((data ?? []).map((t) => [t.record_id, t.fields as Record<string, unknown>]));
  return rows.map((r) => ({ ...localize(r, m.get(r.id)), translated: !!m.get(r.id)?.content }));
}

/** Published integration pages for the public listing (English fallback per field). */
export function usePublishedIntegrationPages(locale: string) {
  const [rows, setRows] = useState<(IntegrationPage & { translated: boolean })[]>([]);
  useEffect(() => {
    let alive = true;
    supabase.from("integration_pages").select("*").eq("is_published", true).order("sort_order").then(async ({ data }) => {
      const out = await withTranslations(data ?? [], locale);
      if (alive) setRows(out);
    });
    return () => { alive = false; };
  }, [locale]);
  return rows;
}

export async function fetchIntegrationPage(slug: string, locale: string) {
  const { data } = await supabase.from("integration_pages").select("*").eq("slug", slug).eq("is_published", true).maybeSingle();
  if (!data) return null;
  return (await withTranslations([data], locale))[0];
}
